import DrawerView from '@app/src/components/Drawer/DrawerView'
import { AvatarSize } from '@app/src/components/Ui/Avatar'
import TextField from '@app/src/components/Ui/TextField'
import CompanyAvatar from '@app/src/pages/ResourceCollection/Collections/CellComponents/CompanyAvatar'
import type { SimpleOrganization } from '@app/src/types/organizations'
import type { SolutionType } from '@app/src/types/resourceExplorer'
import { resolveCompanyLogoImageUrlFromFields } from '@app/src/utils/resolveCompanyLogoImageUrl'
import type { OrganizationSettings } from '@app/src/wf-constants'
import { Stack, Typography } from '@mui/material'
import React from 'react'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import AiScreeningStats from '../AiScreening/AiScreeningStats'
import { useCsmOrganizationSettings } from './useCsmOrganizationSettings'

interface EditOrganizationCsmSettingsDrawerProps {
  closeDrawer: () => void
  organization: SimpleOrganization
  solutionType?: SolutionType
}

const EditOrganizationCsmSettingsDrawer: React.FC<EditOrganizationCsmSettingsDrawerProps> = ({
  closeDrawer,
  organization,
  solutionType,
}) => {
  const { watch, register, handleSubmit, errors, setValue } = useForm<OrganizationSettings>()
  const { formatMessage } = useIntl()
  const { settings, save, isSaving } = useCsmOrganizationSettings(organization, {
    closeDrawer,
    resetForm: () => setValue('allowedProviderScreenings', undefined),
    solutionType,
  })

  const existingAllowedProviderScreenings = settings?.allowedProviderScreenings ?? 0
  const modifyAllowedProviderScreeningsBy = watch('allowedProviderScreenings')

  const onSubmit = async () => {
    const parsedModifyValue = Number(modifyAllowedProviderScreeningsBy)
    await save({ allowedProviderScreenings: existingAllowedProviderScreenings + parsedModifyValue })
  }

  return (
    <DrawerView
      title="Manage AI Screenings Limit"
      buttons={[
        {
          variant: 'contained',
          label: (
            <Typography>
              {(modifyAllowedProviderScreeningsBy ?? 0) >= 0 ? 'Add' : 'Remove'}{' '}
              <Typography component="span" fontWeight="bold">
                {modifyAllowedProviderScreeningsBy}
              </Typography>{' '}
              screenings to the current limit
            </Typography>
          ),
          onClick: handleSubmit(onSubmit),
          disabled: isSaving || Number(modifyAllowedProviderScreeningsBy) === 0,
        },
      ]}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack mx={3}>
          <Stack alignItems="center" mb={6}>
            <CompanyAvatar
              organizationName={organization.name}
              imageUrl={resolveCompanyLogoImageUrlFromFields({
                organizationImageUrl: organization.image?.url,
                websiteAddress: organization.websiteAddress,
              })}
              size={AvatarSize.XXL}
            />
            <Typography variant="h5" mt={2} color="textPrimary">
              {organization.name}
            </Typography>
          </Stack>
          <Typography fontWeight="bold" gutterBottom variant="body1" color="textPrimary">
            Current values:
          </Typography>
          <AiScreeningStats
            allowedProviderScreenings={existingAllowedProviderScreenings}
            totalProviderScreened={settings?.totalProviderScreened ?? 0}
            remainingProviderScreenings={settings?.remainingProviderScreenings ?? 0}
          />

          <Typography fontWeight="bold" gutterBottom variant="body1" color="textPrimary" mt={6}>
            Add (+) or remove (-) to AI screenings limit:
          </Typography>
          <TextField
            hoveringLabel
            inputRef={register({ required: formatMessage({ id: 'form.validation.required' }) })}
            fullWidth
            name="allowedProviderScreenings"
            type="number"
            helperText={errors?.allowedProviderScreenings?.message}
            onClear={(): void => setValue('allowedProviderScreenings', '')}
          />
        </Stack>
      </form>
    </DrawerView>
  )
}

export default EditOrganizationCsmSettingsDrawer
